import type { FlaggedFact } from '@/services/composeService';

export interface FlagRange {
  start: number;
  end: number;
  fact: FlaggedFact;
}

export interface TextSegment {
  text: string;
  fact: FlaggedFact | null;
}

const LETTER = /[\p{L}\p{N}]/u;
const DIGIT = /\d/;

function isBounded(text: string, start: number, end: number, pattern: RegExp): boolean {
  const before = start > 0 ? text[start - 1] : '';
  const after = end < text.length ? text[end] : '';
  return !pattern.test(before) && !pattern.test(after);
}

/** Names match whole words („Tom“ ≠ „Tomate“), numbers whole numbers („15“ ≠ „150“, „2015“). */
function matchesAt(text: string, start: number, end: number, fact: FlaggedFact): boolean {
  if (fact.kind === 'name') return isBounded(text, start, end, LETTER);
  const first = fact.value[0] ?? '';
  const last = fact.value[fact.value.length - 1] ?? '';
  const before = start > 0 ? text[start - 1] : '';
  const after = end < text.length ? text[end] : '';
  return !(DIGIT.test(first) && DIGIT.test(before)) && !(DIGIT.test(last) && DIGIT.test(after));
}

/**
 * Where the flagged values currently stand in `text` — recomputed on every
 * edit, so a value the employee corrected simply stops being marked. Names
 * match as whole words, numbers as whole numbers; overlaps keep the earlier,
 * then the longer value.
 */
export function findFlaggedRanges(text: string, flagged: FlaggedFact[]): FlagRange[] {
  const found: FlagRange[] = [];
  for (const fact of flagged) {
    if (!fact.value) continue;
    let from = 0;
    for (;;) {
      const start = text.indexOf(fact.value, from);
      if (start === -1) break;
      const end = start + fact.value.length;
      if (matchesAt(text, start, end, fact)) found.push({ start, end, fact });
      from = start + 1;
    }
  }
  found.sort((a, b) => a.start - b.start || b.end - a.end);
  const ranges: FlagRange[] = [];
  for (const range of found) {
    const last = ranges[ranges.length - 1];
    if (!last || range.start >= last.end) ranges.push(range);
  }
  return ranges;
}

export function splitByRanges(text: string, ranges: FlagRange[]): TextSegment[] {
  const segments: TextSegment[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start > cursor) segments.push({ text: text.slice(cursor, range.start), fact: null });
    segments.push({ text: text.slice(range.start, range.end), fact: range.fact });
    cursor = range.end;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), fact: null });
  return segments;
}
